import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { AsyncSection, Badge } from "../AgentAnalytics/ds";
import tokens from "../AgentAnalytics/designTokens";
import { useTableStyles } from "../AgentAnalytics/tableStyles";
import { fetchAnalytics } from "./adTemplateApi";
import { AD_TYPE_LABEL, formatLabel, int, relativeDay } from "./formatters";
import FormatBadge from "./FormatBadge";
import AdCardDialog from "./AdCardDialog";

export const AD_COLUMNS = [
  { key: "adName", label: "Ad name" },
  { key: "assistantId", label: "Project" },
  { key: "adType", label: "Type" },
  { key: "format", label: "Format" },
  { key: "status", label: "Status" },
  { key: "spend", label: "Spend", numeric: true },
  { key: "impressions", label: "Impressions", numeric: true },
  { key: "reach", label: "Reach", numeric: true },
  { key: "clicks", label: "Clicks", numeric: true },
  { key: "messages", label: "Messages", numeric: true, sortable: true },
  { key: "leads", label: "Leads", numeric: true, sortable: true },
  { key: "createdAt", label: "Live since", sortable: true },
];

export const AD_EXPORT_COLUMNS = [
  { key: "projectName", label: "Project name" },
  ...AD_COLUMNS.map((c) =>
    c.key === "assistantId" ? { ...c, label: "Project ID" } : c
  ),
];

const useLocalStyles = makeStyles({
  centeredTable: {
    "& td, & th": { textAlign: "center" },
  },
  num: {
    textAlign: "center",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
});

const statusTone = (status) => {
  const value = String(status || "").toUpperCase();
  if (value === "ACTIVE") return "success";
  if (value === "PAUSED") return "warning";
  return "neutral";
};

export default function AdsView({ range, search, refreshKey, onRows, format = "" }) {
  const classes = { ...useTableStyles(), ...useLocalStyles() };
  const [state, setState] = useState({
    loading: true,
    notFound: false,
    items: [],
    total: 0,
    error: null,
  });
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState("createdAt");
  const [dir, setDir] = useState("desc");
  const [openAd, setOpenAd] = useState(null);

  useEffect(() => {
    setPage(0);
  }, [range.from, range.to, search, format]);

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    fetchAnalytics("ads", {
      from: range.from,
      to: range.to,
      page: page + 1,
      sort,
      dir,
      search,
      ...(format ? { format } : {}),
    }).then((r) => {
      if (!alive) return;
      const items = ((r.data && r.data.items) || []).map((ad) => ({
        ...ad,
        format: formatLabel(ad),
      }));
      setState({
        loading: false,
        notFound: r.notFound,
        items,
        total: (r.data && r.data.total) || 0,
        error: r.error,
      });
      if (onRows) onRows(items, AD_EXPORT_COLUMNS);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, page, sort, dir, search, format, refreshKey]);

  const toggleSort = (key) => {
    if (sort === key) {
      setDir(dir === "asc" ? "desc" : "asc");
      return;
    }
    setSort(key);
    setDir("desc");
  };

  return (
    <AsyncSection
      loading={state.loading}
      notFound={state.notFound}
      error={state.error}
      empty={!state.items.length}
      emptyTitle="No live ads from templates yet"
      emptyDescription="Only ads launched after the ad-template link shipped appear here."
    >
      <Box data-testid="ads-table">
        <div className={`${classes.tableWrap} ${classes.centeredTable}`}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {AD_COLUMNS.map((c) => (
                  <TableCell
                    key={c.key}
                    align="center"
                    className={classes.headCell}
                  >
                    {c.sortable ? (
                      <TableSortLabel
                        className={classes.sortLabel}
                        active={sort === c.key}
                        direction={sort === c.key ? dir : "desc"}
                        onClick={() => toggleSort(c.key)}
                      >
                        {c.label}
                      </TableSortLabel>
                    ) : (
                      c.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {state.items.map((ad) => (
                <TableRow
                  key={ad._id}
                  hover
                  className={classes.clickRow}
                  data-testid={`ad-row-${ad._id}`}
                  onClick={() => setOpenAd(ad)}
                >
                    <TableCell className={classes.cell}>
                    <Typography component="div" className={classes.nameText}>
                      {ad.adName || "—"}
                    </Typography>
                    {ad.headline ? (
                      <Typography component="div" className={classes.monoSub}>
                        {ad.headline}
                      </Typography>
                    ) : null}
                    {ad.previewLink ? (
                      <a
                        href={ad.previewLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`ad-preview-${ad._id}`}
                        onClick={(event) => event.stopPropagation()}
                        style={{
                          fontSize: 11.5,
                          fontWeight: 600,
                          color: tokens.link,
                          textDecoration: "none",
                        }}
                      >
                        Preview on Meta
                      </a>
                    ) : null}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <Typography component="div" className={classes.nameText}>
                      {ad.projectName || ad.assistantId}
                    </Typography>
                    {ad.projectName ? (
                      <Typography component="div" className={classes.monoSub}>
                        {ad.assistantId}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {AD_TYPE_LABEL[ad.adType] || ad.adType || "—"}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <FormatBadge item={ad} />
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <Badge tone={statusTone(ad.adEffectiveStatus || ad.adStatus)} subtle>
                      {ad.adEffectiveStatus || ad.adStatus || "—"}
                    </Badge>
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.num}`}>
                    {int(ad.spend)}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.num}`}>
                    {int(ad.impressions)}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.num}`}>
                    {int(ad.reach)}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.num}`}>
                    {int(ad.clicks)}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.num}`}>
                    {int(ad.messages)}
                  </TableCell>
                  <TableCell className={`${classes.cell} ${classes.num}`}>
                    {int(ad.leads)}
                  </TableCell>
                  <TableCell className={classes.cell}>{relativeDay(ad.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <TablePagination
          component="div"
          count={state.total}
          page={page}
          onChangePage={(event, next) => setPage(next)}
          rowsPerPage={25}
          rowsPerPageOptions={[25]}
        />

        <AdCardDialog ad={openAd} onClose={() => setOpenAd(null)} />
      </Box>
    </AsyncSection>
  );
}
